export const ERROR_CODES: { [key: string]: string } = {
  "auth/email-already-in-use": "This email is already in use. Switch to sign in mode, or create a different account.",
};

export const PLAN_KEYS: { [key: string]: string } = {
  PREMIUM: "PREMIUM",
};

export type PlanKey = "MINI_ARTIST" | "CREATIVE_KID" | "YOUNG_DA_VINCI" | "MASTER_CRAFTER" | "PICASSO";

export const PLAN_PRICE_IDS: Record<PlanKey, string | undefined> = {
  MINI_ARTIST: process.env.STRIPE_MINI_ARTIST,
  CREATIVE_KID: process.env.STRIPE_CREATIVE_KID,
  YOUNG_DA_VINCI: process.env.STRIPE_YOUNG_DA_VINCI,
  MASTER_CRAFTER: process.env.STRIPE_MASTER_CRAFTER,
  PICASSO: process.env.STRIPE_PICASSO,
};

// Import pricing from centralized configuration
import { PRICING } from "./pricing";

export const plans = [
  {
    key: "MINI_ARTIST",
    name: "Mini Artist",
    price: PRICING.PLANS.MINI_ARTIST.PRICE,
    credits: PRICING.PLANS.MINI_ARTIST.CREDITS,
    popular: false,
  },
  {
    key: "CREATIVE_KID",
    name: "Creative Kid",
    price: PRICING.PLANS.CREATIVE_KID.PRICE,
    credits: PRICING.PLANS.CREATIVE_KID.CREDITS,
    popular: false,
  },
  {
    key: "YOUNG_DA_VINCI",
    name: "Young Da Vinci",
    price: PRICING.PLANS.YOUNG_DA_VINCI.PRICE,
    credits: PRICING.PLANS.YOUNG_DA_VINCI.CREDITS,
    popular: true,
  },
  {
    key: "MASTER_CRAFTER",
    name: "Master Crafter",
    price: PRICING.PLANS.MASTER_CRAFTER.PRICE,
    credits: PRICING.PLANS.MASTER_CRAFTER.CREDITS,
    popular: false,
  },
  {
    key: "PICASSO",
    name: "Picasso",
    price: PRICING.PLANS.PICASSO.PRICE,
    credits: PRICING.PLANS.PICASSO.CREDITS,
    popular: false,
  },
];

export type Color = {
  key: string;
  name: string;
  hex: string;
  temperature: string;
  intensity: string;
  undertone: string;
};

export type Subseason = {
  description: string;
  emoji: string;
  primaryColors: Color[];
  neutrals: Color[];
  complementaryColors: Color[];
  avoidColors: Color[];
  sampleClothing: string;
  characteristics: string[];
};

export type Season = {
  emoji: string;
  subseasons: {
    [key: string]: Subseason;
  };
};

export type ColorSeasons = {
  [key: string]: Season;
};

export const colorSeasons: ColorSeasons = {
  SPRING: {
    emoji: "🌱",
    subseasons: {
      LIGHT: {
        emoji: "🌸",
        description: `Light Spring is like a fresh breath on a bright morning. Its palette celebrates the gentle warmth of early spring by combining soft pastel tones with light, vibrant accents. This season embraces colors that feel airy and luminous—reflecting a natural radiance that enhances delicate features. The hues work together in a graceful interplay of softness and clarity, evoking the cheerful, rejuvenating spirit of a blossoming spring day.`,

        primaryColors: [
          {
            key: "APRICOT",
            name: "Apricot",
            hex: "#FBCEB1",
            temperature: "warm",
            intensity: "light",
            undertone: "golden",
          },
          { key: "PEACH", name: "Peach", hex: "#FFDAB3", temperature: "warm", intensity: "light", undertone: "golden" },
          {
            key: "LIGHT_CORAL",
            name: "Light Coral",
            hex: "#F88379",
            temperature: "warm",
            intensity: "light",
            undertone: "red",
          },
          {
            key: "BUTTERCREAM",
            name: "Buttercream",
            hex: "#FFF1C1",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "DAFFODIL",
            name: "Daffodil",
            hex: "#FFFF99",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "MINT_GREEN",
            name: "Mint Green",
            hex: "#C4E1C1",
            temperature: "cool",
            intensity: "light",
            undertone: "green",
          },
          {
            key: "LIGHT_AQUA",
            name: "Light Aqua",
            hex: "#B3E3E2",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "SUNLIGHT_YELLOW",
            name: "Sunlight Yellow",
            hex: "#FFE28A",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "BLUSH_PINK",
            name: "Blush Pink",
            hex: "#FADADD",
            temperature: "warm",
            intensity: "light",
            undertone: "pink",
          },
          {
            key: "SOFT_MELON",
            name: "Soft Melon",
            hex: "#FFB7A5",
            temperature: "warm",
            intensity: "light",
            undertone: "red",
          },
          {
            key: "LIGHT_PERIWINKLE",
            name: "Light Periwinkle",
            hex: "#D6C9FF",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "PALE_GOLD",
            name: "Pale Gold",
            hex: "#F7E7CE",
            temperature: "warm",
            intensity: "light",
            undertone: "golden",
          },
        ],
        neutrals: [
          { key: "CREAM", name: "Cream", hex: "#FFFDD0", temperature: "warm", intensity: "light", undertone: "yellow" },
          {
            key: "WARM_BEIGE",
            name: "Warm Beige",
            hex: "#F5F5DC",
            temperature: "warm",
            intensity: "light",
            undertone: "golden",
          },
          {
            key: "SOFT_CAMEL",
            name: "Soft Camel",
            hex: "#D2B48C",
            temperature: "warm",
            intensity: "light",
            undertone: "golden",
          },
          {
            key: "PALE_TAUPE",
            name: "Pale Taupe",
            hex: "#BC987E",
            temperature: "warm",
            intensity: "light",
            undertone: "brown",
          },
        ],
        complementaryColors: [
          {
            key: "LIGHT_MINT",
            name: "Light Mint",
            hex: "#98FF98",
            temperature: "cool",
            intensity: "light",
            undertone: "green",
          },
          { key: "LILAC", name: "Lilac", hex: "#C8A2C8", temperature: "cool", intensity: "light", undertone: "purple" },
          {
            key: "SKY_BLUE",
            name: "Sky Blue",
            hex: "#87CEEB",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "PERIWINKLE",
            name: "Periwinkle",
            hex: "#CCCCFF",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
        ],
        avoidColors: [
          { key: "BLACK", name: "Black", hex: "#000000", temperature: "cool", intensity: "deep", undertone: "neutral" },
          {
            key: "DEEP_NAVY",
            name: "Deep Navy",
            hex: "#000080",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "CHARCOAL",
            name: "Charcoal",
            hex: "#36454F",
            temperature: "cool",
            intensity: "dark",
            undertone: "blue",
          },
          {
            key: "BURGUNDY",
            name: "Burgundy",
            hex: "#800020",
            temperature: "cool",
            intensity: "deep",
            undertone: "red",
          },
        ],
        sampleClothing: "Pastel blouses, light wash jeans, cream colored sweaters",
        characteristics: ["light", "warm", "fresh", "delicate"],
      },
      TRUE: {
        emoji: "🌼",
        description: `True Spring individuals possess vivid and bright colouring.
The True Spring palette is bold and dynamic, featuring clear, vibrant hues that highlight natural radiance. It bridges the brightness of True Spring with the clarity of True Winter.
Unlike other Spring sub-seasons, True Spring embraces high-contrast, saturated colours that bring energy and playfulness to your wardrobe.`,
        primaryColors: [
          {
            key: "AMBER",
            name: "Amber",
            hex: "#FFBF00",
            temperature: "warm",
            intensity: "bright",
            undertone: "golden",
          },
          {
            key: "AZURE",
            name: "Azure",
            hex: "#007FFF",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "CORAL",
            name: "Coral",
            hex: "#FF6F61",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "EMERALD",
            name: "Emerald",
            hex: "#4CBB17",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          {
            key: "WARM_POPPY",
            name: "Warm Poppy",
            hex: "#FF6347",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "GOLDEN",
            name: "Golden",
            hex: "#FFD700",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          {
            key: "JADE",
            name: "Jade",
            hex: "#00A86B",
            temperature: "cool",
            intensity: "bright",
            undertone: "green",
          },
          {
            key: "BRIGHT_TANGERINE",
            name: "Bright Tangerine",
            hex: "#FF9966",
            temperature: "warm",
            intensity: "bright",
            undertone: "orange",
          },
          {
            key: "ORANGE",
            name: "Orange",
            hex: "#FFA500",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "WARM_WATERMELON",
            name: "Warm Watermelon",
            hex: "#FF5C7A",
            temperature: "warm",
            intensity: "bright",
            undertone: "pink",
          },
          {
            key: "AMETHYST",
            name: "Amethyst",
            hex: "#9966CC",
            temperature: "warm",
            intensity: "bright",
            undertone: "purple",
          },
          {
            key: "TURQUOISE",
            name: "Turquoise",
            hex: "#40E0D0",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
        ],
        neutrals: [
          {
            key: "WARM_IVORY",
            name: "Warm Ivory",
            hex: "#FFF8E1",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "LIGHT_CAMEL",
            name: "Light Camel",
            hex: "#D2B48C",
            temperature: "warm",
            intensity: "medium",
            undertone: "golden",
          },
          {
            key: "SAND",
            name: "Sand",
            hex: "#F4E1C1",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "SOFT_BEIGE",
            name: "Soft Beige",
            hex: "#F5F5DC",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
        ],
        complementaryColors: [
          {
            key: "AQUA_BLUE",
            name: "Aqua Blue",
            hex: "#00FFFF",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "BRIGHT_LAVENDER",
            name: "Bright Lavender",
            hex: "#BF94E4",
            temperature: "cool",
            intensity: "bright",
            undertone: "purple",
          },
          {
            key: "PERIWINKLE",
            name: "Periwinkle",
            hex: "#CCCCFF",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "BRIGHT_BERRY",
            name: "Bright Berry",
            hex: "#D2386C",
            temperature: "cool",
            intensity: "bright",
            undertone: "red",
          },
        ],
        avoidColors: [
          {
            key: "DULL_OLIVE",
            name: "Dull Olive",
            hex: "#808000",
            temperature: "warm",
            intensity: "muted",
            undertone: "green",
          },
          {
            key: "MUDDY_BROWN",
            name: "Muddy Brown",
            hex: "#8B4513",
            temperature: "warm",
            intensity: "muted",
            undertone: "brown",
          },
          {
            key: "DUSTY_ROSE",
            name: "Dusty Rose",
            hex: "#C08081",
            temperature: "cool",
            intensity: "muted",
            undertone: "pink",
          },
          {
            key: "CHARCOAL_GREY",
            name: "Charcoal Grey",
            hex: "#36454F",
            temperature: "cool",
            intensity: "dark",
            undertone: "blue",
          },
        ],
        sampleClothing: "Bright floral dresses, crisp white shirts, clear colored accessories",
        characteristics: ["clear", "warm", "vibrant", "fresh"],
      },
      BRIGHT: {
        emoji: "☀️",
        description: `Bright Spring individuals have bold and vibrant hues that create a lively and energetic appearance.
The Bright Spring palette features high-contrast, saturated colours that bring energy and playfulness to your wardrobe.`,
        primaryColors: [
          {
            key: "NEON_CORAL",
            name: "Neon Coral",
            hex: "#FF5E5B",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "BRIGHT_BLUE",
            name: "Bright Blue",
            hex: "#1E90FF",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "CLEAR_ORANGE",
            name: "Clear Orange",
            hex: "#FF9900",
            temperature: "warm",
            intensity: "bright",
            undertone: "orange",
          },
          {
            key: "BRIGHT_PEACH",
            name: "Bright Peach",
            hex: "#FF8C69",
            temperature: "warm",
            intensity: "bright",
            undertone: "orange",
          },
          {
            key: "GRASS_GREEN",
            name: "Grass Green",
            hex: "#6CC24A",
            temperature: "warm",
            intensity: "bright",
            undertone: "green",
          },
          {
            key: "TURQUOISE_BLUE",
            name: "Turquoise Blue",
            hex: "#00CED1",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "WARM_PINK",
            name: "Warm Pink",
            hex: "#FF69B4",
            temperature: "warm",
            intensity: "bright",
            undertone: "pink",
          },
          {
            key: "SUNSHINE_YELLOW",
            name: "Sunshine Yellow",
            hex: "#FFD500",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          {
            key: "BRIGHT_FUCHSIA",
            name: "Bright Fuchsia",
            hex: "#E0218A",
            temperature: "cool",
            intensity: "bright",
            undertone: "pink",
          },
          {
            key: "HOT_TOMATO",
            name: "Hot Tomato",
            hex: "#FF6347",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "GOLDENROD",
            name: "Goldenrod",
            hex: "#DAA520",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          {
            key: "BRIGHT_LIME_GREEN",
            name: "Bright Lime Green",
            hex: "#A7FC00",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
        ],
        neutrals: [
          {
            key: "WARM_WHITE",
            name: "Warm White",
            hex: "#FFF8E7",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "SOFT_CAMEL",
            name: "Soft Camel",
            hex: "#C19A6B",
            temperature: "warm",
            intensity: "medium",
            undertone: "golden",
          },
          {
            key: "BUTTERMILK",
            name: "Buttermilk",
            hex: "#FFF1C1",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "LIGHT_TAN",
            name: "Light Tan",
            hex: "#D2B48C",
            temperature: "warm",
            intensity: "light",
            undertone: "golden",
          },
        ],
        complementaryColors: [
          {
            key: "BRIGHT_LILAC",
            name: "Bright Lilac",
            hex: "#C8A2C8",
            temperature: "cool",
            intensity: "bright",
            undertone: "purple",
          },
          {
            key: "POPPY_RED",
            name: "Poppy Red",
            hex: "#FF3855",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "CLEAR_BLUE",
            name: "Clear Blue",
            hex: "#0096FF",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "BRIGHT_PEACH",
            name: "Bright Peach",
            hex: "#FF9F80",
            temperature: "warm",
            intensity: "bright",
            undertone: "orange",
          },
        ],
        avoidColors: [
          {
            key: "MUTED_OLIVE",
            name: "Muted Olive",
            hex: "#808000",
            temperature: "warm",
            intensity: "muted",
            undertone: "green",
          },
          {
            key: "MUDDY_BROWN",
            name: "Muddy Brown",
            hex: "#8B4513",
            temperature: "warm",
            intensity: "muted",
            undertone: "brown",
          },
          {
            key: "DULL_PURPLE",
            name: "Dull Purple",
            hex: "#8B668B",
            temperature: "cool",
            intensity: "muted",
            undertone: "purple",
          },
          {
            key: "DARK_GREY",
            name: "Dark Grey",
            hex: "#2F4F4F",
            temperature: "cool",
            intensity: "dark",
            undertone: "blue",
          },
        ],
        sampleClothing: "Bold colored blazers, vibrant tops, statement jewelry",
        characteristics: ["bright", "high-contrast", "energetic", "bold"],
      },
    },
  },
  SUMMER: {
    emoji: "☀️",
    subseasons: {
      LIGHT: {
        emoji: "🌞",
        description: `Light Summer individuals have soft and muted hues that create a calm and serene aura.
The Light Summer palette embraces cool, pastel shades that complement the natural subtlety of Light Summer features.
While it shares some similarities with Light Spring, Light Summer leans distinctly cool, emphasizing an airy and delicate look.`,
        primaryColors: [
          {
            key: "MISTY_BLUE",
            name: "Misty Blue",
            hex: "#B4CFEC",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "POWDER_PINK",
            name: "Powder Pink",
            hex: "#FFD1DC",
            temperature: "cool",
            intensity: "light",
            undertone: "pink",
          },
          {
            key: "SEAFOAM",
            name: "Seafoam",
            hex: "#B5EAD7",
            temperature: "cool",
            intensity: "light",
            undertone: "green",
          },
          { key: "LILAC", name: "Lilac", hex: "#C8A2C8", temperature: "cool", intensity: "light", undertone: "purple" },
          {
            key: "PETAL_PINK",
            name: "Petal Pink",
            hex: "#F4B6C2",
            temperature: "cool",
            intensity: "light",
            undertone: "pink",
          },
          {
            key: "PALE_LEMON",
            name: "Pale Lemon",
            hex: "#F5F2B8",
            temperature: "cool",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "SOFT_LAVENDER",
            name: "Soft Lavender",
            hex: "#E6E6FA",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "POWDER_BLUE",
            name: "Powder Blue",
            hex: "#B0E0E6",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "SOFT_MINT",
            name: "Soft Mint",
            hex: "#A8D8B9",
            temperature: "cool",
            intensity: "light",
            undertone: "green",
          },
          {
            key: "ROSE_QUARTZ",
            name: "Rose Quartz",
            hex: "#F7CAC9",
            temperature: "cool",
            intensity: "light",
            undertone: "pink",
          },
          {
            key: "SKY_BLUE",
            name: "Sky Blue",
            hex: "#87CEEB",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "PALE_PERIWINKLE",
            name: "Pale Periwinkle",
            hex: "#CCCCFF",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
        ],
        neutrals: [
          {
            key: "SILVER_GREY",
            name: "Silver Grey",
            hex: "#C0C0C0",
            temperature: "cool",
            intensity: "light",
            undertone: "grey",
          },
          {
            key: "LIGHT_TAUPE",
            name: "Light Taupe",
            hex: "#D8CFC4",
            temperature: "cool",
            intensity: "light",
            undertone: "grey",
          },
          {
            key: "PALE_IVORY",
            name: "Pale Ivory",
            hex: "#FFFFF0",
            temperature: "cool",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "SOFT_WHITE",
            name: "Soft White",
            hex: "#F5F5F5",
            temperature: "cool",
            intensity: "light",
            undertone: "neutral",
          },
        ],
        complementaryColors: [
          {
            key: "SOFT_MINT",
            name: "Soft Mint",
            hex: "#AAF0D1",
            temperature: "cool",
            intensity: "light",
            undertone: "green",
          },
          {
            key: "SOFT_PINK",
            name: "Soft Pink",
            hex: "#FFB6C1",
            temperature: "cool",
            intensity: "light",
            undertone: "pink",
          },
          {
            key: "PALE_AQUA",
            name: "Pale Aqua",
            hex: "#D4F1F9",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "WISTERIA",
            name: "Wisteria",
            hex: "#C9A0DC",
            temperature: "cool",
            intensity: "light",
            undertone: "purple",
          },
        ],
        avoidColors: [
          { key: "BLACK", name: "Black", hex: "#000000", temperature: "cool", intensity: "deep", undertone: "neutral" },
          {
            key: "DARK_NAVY",
            name: "Dark Navy",
            hex: "#000080",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "BRIGHT_ORANGE",
            name: "Bright Orange",
            hex: "#FF6600",
            temperature: "warm",
            intensity: "bright",
            undertone: "orange",
          },
          {
            key: "WARM_CAMEL",
            name: "Warm Camel",
            hex: "#C19A6B",
            temperature: "warm",
            intensity: "medium",
            undertone: "yellow",
          },
        ],
        sampleClothing: "Light, airy fabrics in cool pastel shades, such as breezy dresses and light knit cardigans",
        characteristics: ["soft", "cool", "delicate", "muted"],
      },
      TRUE: {
        emoji: "🌻",
        description: `True Summer individuals blend cool and muted tones, creating a harmonious and serene appearance. Their coloring is balanced, with neither overly bright nor deeply saturated hues.
The True Summer palette evokes a sense of calm and elegance, featuring shades that harmonize seamlessly with their natural features. Positioned between Soft Summer and True Summer, this sub-season perfectly balances subtlety and sophistication.

Unlike its counterparts, True Summer embraces a gentle contrast, ensuring their style remains both refined and approachable.`,
        primaryColors: [
          {
            key: "DUSTY_ROSE",
            name: "Dusty Rose",
            hex: "#D4A5A5",
            temperature: "cool",
            intensity: "soft",
            undertone: "rose",
          },
          { key: "GREY_SAGE", name: "Grey Sage", hex: "#8FA79A", temperature: "cool", intensity: "soft", undertone: "green" },
          {
            key: "SLATE_BLUE",
            name: "Slate Blue",
            hex: "#5D6D8E",
            temperature: "cool",
            intensity: "soft",
            undertone: "blue",
          },
          { key: "MAUVE", name: "Mauve", hex: "#C9A0DC", temperature: "cool", intensity: "soft", undertone: "purple" },
          { key: "PEARL_GREY", name: "Pearl Grey", hex: "#AEB4BC", temperature: "cool", intensity: "soft", undertone: "neutral" },
          {
            key: "CHARCOAL_BLUE",
            name: "Charcoal Blue",
            hex: "#4A545E",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "SMOKY_BLUE",
            name: "Smoky Blue",
            hex: "#6B7B8C",
            temperature: "cool",
            intensity: "soft",
            undertone: "blue",
          },
          {
            key: "DUSTY_LAVENDER",
            name: "Dusty Lavender",
            hex: "#B39EB5",
            temperature: "cool",
            intensity: "soft",
            undertone: "purple",
          },
          {
            key: "SPRUCE_GREEN",
            name: "Spruce Green",
            hex: "#587D71",
            temperature: "cool",
            intensity: "soft",
            undertone: "green",
          },
          {
            key: "ROSEWOOD",
            name: "Rosewood",
            hex: "#B38481",
            temperature: "cool",
            intensity: "soft",
            undertone: "red",
          },
          {
            key: "SLATE_GRAY",
            name: "Slate Gray",
            hex: "#708090",
            temperature: "cool",
            intensity: "soft",
            undertone: "blue",
          },
          {
            key: "DUSTY_TEAL",
            name: "Dusty Teal",
            hex: "#5E7D7E",
            temperature: "cool",
            intensity: "soft",
            undertone: "green",
          },
        ],
        neutrals: [
          {
            key: "SOFT_GREY",
            name: "Soft Grey",
            hex: "#B0B0B0",
            temperature: "cool",
            intensity: "soft",
            undertone: "grey",
          },
          {
            key: "DOVE_GREY",
            name: "Dove Grey",
            hex: "#D5D5D5",
            temperature: "cool",
            intensity: "soft",
            undertone: "grey",
          },
          {
            key: "SOFT_WHITE",
            name: "Soft White",
            hex: "#F8F8F8",
            temperature: "cool",
            intensity: "soft",
            undertone: "neutral",
          },
          {
            key: "MISTY_TAUPE",
            name: "Misty Taupe",
            hex: "#CCC5B9",
            temperature: "neutral",
            intensity: "soft",
            undertone: "grey",
          },
        ],
        complementaryColors: [
          {
            key: "DUSTY_ROSE",
            name: "Dusty Rose",
            hex: "#C08081",
            temperature: "cool",
            intensity: "soft",
            undertone: "pink",
          },
          {
            key: "MUTED_LILAC",
            name: "Muted Lilac",
            hex: "#B39EB5",
            temperature: "cool",
            intensity: "soft",
            undertone: "purple",
          },
          {
            key: "FADED_BLUE",
            name: "Faded Blue",
            hex: "#7A9BAE",
            temperature: "cool",
            intensity: "soft",
            undertone: "blue",
          },
          {
            key: "SOFT_MOSS",
            name: "Soft Moss",
            hex: "#A8B4A5",
            temperature: "neutral",
            intensity: "soft",
            undertone: "green",
          },
        ],
        avoidColors: [
          {
            key: "BRIGHT_RED",
            name: "Bright Red",
            hex: "#FF0000",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "WARM_MUSTARD",
            name: "Warm Mustard",
            hex: "#FFDB58",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          {
            key: "DARK_CHOCOLATE",
            name: "Dark Chocolate",
            hex: "#381819",
            temperature: "warm",
            intensity: "deep",
            undertone: "brown",
          },
          {
            key: "PURE_BLACK",
            name: "Pure Black",
            hex: "#000000",
            temperature: "cool",
            intensity: "deep",
            undertone: "neutral",
          },
        ],
        sampleClothing: "Muted floral prints, soft denim jackets, dusty rose accessories",
        characteristics: ["muted", "cool", "subtle", "sophisticated"],
      },
      COOL: {
        emoji: "🌤️",
        description: `Cool Summer individuals have cool, muted hues that create a cool and serene appearance.
The Cool Summer palette features cool, muted shades that complement the natural subtlety of Cool Summer features.`,
        primaryColors: [
          { key: "MEDIUM_NAVY", name: "Medium Navy", hex: "#3B5998", temperature: "cool", intensity: "medium", undertone: "blue" },
          { key: "TEAL", name: "Teal", hex: "#3E8E8E", temperature: "cool", intensity: "medium", undertone: "green" },
          { key: "PLUM", name: "Plum", hex: "#8E4585", temperature: "cool", intensity: "medium", undertone: "purple" },
          {
            key: "STEEL_GRAY",
            name: "Steel Gray",
            hex: "#71797E",
            temperature: "cool",
            intensity: "muted",
            undertone: "blue",
          },
          {
            key: "MUTED_ROSE",
            name: "Muted Rose",
            hex: "#C76B8A",
            temperature: "cool",
            intensity: "medium",
            undertone: "pink",
          },
          {
            key: "COOL_SPRUCE",
            name: "Cool Spruce",
            hex: "#4A7C6F",
            temperature: "cool",
            intensity: "medium",
            undertone: "green",
          },
          { key: "BLUE_VIOLET", name: "Blue Violet", hex: "#7366BD", temperature: "cool", intensity: "medium", undertone: "blue" },
          {
            key: "OCEAN_BLUE",
            name: "Ocean Blue",
            hex: "#4F84C4",
            temperature: "cool",
            intensity: "medium",
            undertone: "blue",
          },
          {
            key: "COOL_PLUM",
            name: "Cool Plum",
            hex: "#6B4570",
            temperature: "cool",
            intensity: "medium",
            undertone: "purple",
          },
          {
            key: "MUTED_TEAL",
            name: "Muted Teal",
            hex: "#5B8A8A",
            temperature: "cool",
            intensity: "medium",
            undertone: "green",
          },
          {
            key: "COOL_SLATE",
            name: "Cool Slate",
            hex: "#5A6D7A",
            temperature: "cool",
            intensity: "medium",
            undertone: "grey",
          },
          {
            key: "RASPBERRY",
            name: "Raspberry",
            hex: "#9B3A62",
            temperature: "cool",
            intensity: "medium",
            undertone: "pink",
          },
        ],
        neutrals: [
          {
            key: "SILVER",
            name: "Silver",
            hex: "#C0C0C0",
            temperature: "cool",
            intensity: "medium",
            undertone: "grey",
          },
          {
            key: "PEWTER",
            name: "Pewter",
            hex: "#899499",
            temperature: "cool",
            intensity: "medium",
            undertone: "grey",
          },
          {
            key: "SOFT_NAVY",
            name: "Soft Navy",
            hex: "#4C516D",
            temperature: "cool",
            intensity: "medium",
            undertone: "blue",
          },
          {
            key: "FROSTED_WHITE",
            name: "Frosted White",
            hex: "#F0F0F0",
            temperature: "cool",
            intensity: "light",
            undertone: "neutral",
          },
        ],
        complementaryColors: [
          {
            key: "COOL_PLUM",
            name: "Cool Plum",
            hex: "#9A5F82",
            temperature: "cool",
            intensity: "medium",
            undertone: "purple",
          },
          {
            key: "SOFT_BLUE",
            name: "Soft Blue",
            hex: "#7EC8E3",
            temperature: "cool",
            intensity: "medium",
            undertone: "blue",
          },
          {
            key: "MUTED_BERRY",
            name: "Muted Berry",
            hex: "#A52A5E",
            temperature: "cool",
            intensity: "medium",
            undertone: "pink",
          },
          {
            key: "DUSKY_LILAC",
            name: "Dusky Lilac",
            hex: "#A398B4",
            temperature: "cool",
            intensity: "medium",
            undertone: "purple",
          },
        ],
        avoidColors: [
          {
            key: "BRIGHT_ORANGE",
            name: "Bright Orange",
            hex: "#FF6600",
            temperature: "warm",
            intensity: "bright",
            undertone: "orange",
          },
          {
            key: "WARM_CORAL",
            name: "Warm Coral",
            hex: "#FF7F50",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "WARM_GOLD",
            name: "Warm Gold",
            hex: "#FFD700",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          {
            key: "RICH_CAMEL",
            name: "Rich Camel",
            hex: "#C19A6B",
            temperature: "warm",
            intensity: "medium",
            undertone: "yellow",
          },
        ],
        sampleClothing: "Navy blazers, cool pink blouses, grey trousers",
        characteristics: ["cool", "crisp", "clear", "blue-based"],
      },
    },
  },
  AUTUMN: {
    emoji: "🍂",
    subseasons: {
      SOFT: {
        emoji: "🍁",
        description: `Soft Autumn individuals radiate warmth and subtlety.
The Soft Autumn palette is characterised by earthy, muted tones that evoke the richness of autumn leaves.`,
        primaryColors: [
          {
            key: "MUTED_GOLD",
            name: "Muted Gold",
            hex: "#D4AF37",
            temperature: "warm",
            intensity: "soft",
            undertone: "yellow",
          },
          {
            key: "DUSTY_CORAL",
            name: "Dusty Coral",
            hex: "#D8A48F",
            temperature: "warm",
            intensity: "soft",
            undertone: "orange",
          },
          { key: "SAGE", name: "Sage", hex: "#A3AA8B", temperature: "warm", intensity: "soft", undertone: "green" },
          {
            key: "SOFT_RUST",
            name: "Soft Rust",
            hex: "#C87C5A",
            temperature: "warm",
            intensity: "soft",
            undertone: "red",
          },
          { key: "OLIVE", name: "Olive", hex: "#808000", temperature: "warm", intensity: "soft", undertone: "green" },
          {
            key: "WARM_TAUPE",
            name: "Warm Taupe",
            hex: "#D2B1A3",
            temperature: "warm",
            intensity: "soft",
            undertone: "brown",
          },
          {
            key: "DUSTY_PLUM",
            name: "Dusty Plum",
            hex: "#9B6A6C",
            temperature: "warm",
            intensity: "soft",
            undertone: "red",
          },
          { key: "WHEAT", name: "Wheat", hex: "#F5DEB3", temperature: "warm", intensity: "light", undertone: "yellow" },
          {
            key: "TERRACOTTA",
            name: "Terracotta",
            hex: "#E2725B",
            temperature: "warm",
            intensity: "medium",
            undertone: "orange",
          },
          { key: "MOCHA", name: "Mocha", hex: "#967969", temperature: "warm", intensity: "medium", undertone: "brown" },
          {
            key: "MOSS_GREEN",
            name: "Moss Green",
            hex: "#8A9A5B",
            temperature: "warm",
            intensity: "medium",
            undertone: "green",
          },
          {
            key: "SOFT_BRONZE",
            name: "Soft Bronze",
            hex: "#B08D57",
            temperature: "warm",
            intensity: "medium",
            undertone: "brown",
          },
        ],
        neutrals: [
          {
            key: "SOFT_BEIGE",
            name: "Soft Beige",
            hex: "#D8CAB8",
            temperature: "warm",
            intensity: "soft",
            undertone: "yellow",
          },
          {
            key: "WARM_TAUPE",
            name: "Warm Taupe",
            hex: "#B49F8C",
            temperature: "warm",
            intensity: "soft",
            undertone: "brown",
          },
          {
            key: "MUTED_CAMEL",
            name: "Muted Camel",
            hex: "#C19A6B",
            temperature: "warm",
            intensity: "soft",
            undertone: "yellow",
          },
          {
            key: "SOFT_OLIVE",
            name: "Soft Olive",
            hex: "#A9A17A",
            temperature: "warm",
            intensity: "soft",
            undertone: "green",
          },
        ],
        complementaryColors: [
          {
            key: "DUSTY_AQUA",
            name: "Dusty Aqua",
            hex: "#88A0A8",
            temperature: "cool",
            intensity: "soft",
            undertone: "blue",
          },
          {
            key: "PALE_BLUE",
            name: "Pale Blue",
            hex: "#AFCEDC",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "FADED_LAVENDER",
            name: "Faded Lavender",
            hex: "#B9AEDC",
            temperature: "cool",
            intensity: "soft",
            undertone: "purple",
          },
          {
            key: "SOFT_PERIWINKLE",
            name: "Soft Periwinkle",
            hex: "#C3CDE6",
            temperature: "cool",
            intensity: "soft",
            undertone: "blue",
          },
        ],
        avoidColors: [
          {
            key: "BRIGHT_WHITE",
            name: "Bright White",
            hex: "#FFFFFF",
            temperature: "cool",
            intensity: "light",
            undertone: "neutral",
          },
          {
            key: "JET_BLACK",
            name: "Jet Black",
            hex: "#000000",
            temperature: "cool",
            intensity: "deep",
            undertone: "neutral",
          },
          {
            key: "COOL_FUCHSIA",
            name: "Cool Fuchsia",
            hex: "#FF00FF",
            temperature: "cool",
            intensity: "bright",
            undertone: "pink",
          },
          {
            key: "COOL_NAVY",
            name: "Cool Navy",
            hex: "#000080",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
        ],
        sampleClothing: "Muted floral prints, earthy toned sweaters, soft brown leather jackets",
        characteristics: ["muted", "warm", "earthy", "rich"],
      },
      TRUE: {
        emoji: "🧡",
        description: `True Warm Autumn individuals are characterized by rich and vibrant coloring. Their palette includes deep, earthy hues that highlight their dynamic and robust appearance.
The True Warm Autumn palette draws inspiration from the rich tones of a vibrant autumn forest, offering a bold and lively range of colors. Positioned as the most intense Autumn sub-season, it embraces the full warmth and depth of autumnal shades.

Unlike Soft Autumn, True Warm Autumn thrives on higher color saturation and stronger contrasts, ensuring their style is both striking and effortlessly stylish.`,
        primaryColors: [
          {
            key: "GOLDENROD",
            name: "Goldenrod",
            hex: "#DAA520",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          {
            key: "PUMPKIN",
            name: "Pumpkin",
            hex: "#FF7518",
            temperature: "warm",
            intensity: "bright",
            undertone: "orange",
          },
          { key: "COPPER", name: "Copper", hex: "#B87333", temperature: "warm", intensity: "medium", undertone: "red" },
          {
            key: "VERMILION",
            name: "Vermilion",
            hex: "#E34234",
            temperature: "warm",
            intensity: "bright",
            undertone: "red",
          },
          {
            key: "WARM_SAFFRON",
            name: "Warm Saffron",
            hex: "#F4C430",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          { key: "RUST", name: "Rust", hex: "#B7410E", temperature: "warm", intensity: "medium", undertone: "orange" },
          {
            key: "DEEP_TURQUOISE",
            name: "Deep Turquoise",
            hex: "#317873",
            temperature: "neutral",
            intensity: "deep",
            undertone: "green",
          },
          {
            key: "WARM_MOSS",
            name: "Warm Moss",
            hex: "#8A9A5B",
            temperature: "warm",
            intensity: "medium",
            undertone: "green",
          },
          {
            key: "SUNFLOWER",
            name: "Sunflower",
            hex: "#FFC512",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
          {
            key: "BRONZE",
            name: "Bronze",
            hex: "#CD7F32",
            temperature: "warm",
            intensity: "medium",
            undertone: "golden",
          },
          {
            key: "WARM_OLIVE",
            name: "Warm Olive",
            hex: "#6B8E23",
            temperature: "warm",
            intensity: "medium",
            undertone: "green",
          },
          {
            key: "SIENNA",
            name: "Sienna",
            hex: "#A0522D",
            temperature: "warm",
            intensity: "deep",
            undertone: "orange",
          },
        ],
        neutrals: [
          {
            key: "CAMEL",
            name: "Camel",
            hex: "#C19A6B",
            temperature: "warm",
            intensity: "medium",
            undertone: "yellow",
          },
          {
            key: "WARM_TAUPE",
            name: "Warm Taupe",
            hex: "#B49F8C",
            temperature: "warm",
            intensity: "medium",
            undertone: "brown",
          },
          {
            key: "GOLDEN_BEIGE",
            name: "Golden Beige",
            hex: "#D8CAB8",
            temperature: "warm",
            intensity: "medium",
            undertone: "yellow",
          },
          {
            key: "WARM_OLIVE",
            name: "Warm Olive",
            hex: "#A9A17A",
            temperature: "warm",
            intensity: "medium",
            undertone: "green",
          },
        ],
        complementaryColors: [
          {
            key: "SOFT_AQUA",
            name: "Soft Aqua",
            hex: "#88C8C8",
            temperature: "cool",
            intensity: "soft",
            undertone: "blue",
          },
          {
            key: "LIGHT_PERIWINKLE",
            name: "Light Periwinkle",
            hex: "#CCCCFF",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "MUTED_LAVENDER",
            name: "Muted Lavender",
            hex: "#B9AEDC",
            temperature: "cool",
            intensity: "soft",
            undertone: "purple",
          },
          {
            key: "COOL_MINT",
            name: "Cool Mint",
            hex: "#98FF98",
            temperature: "cool",
            intensity: "light",
            undertone: "green",
          },
        ],
        avoidColors: [
          {
            key: "BRIGHT_WHITE",
            name: "Bright White",
            hex: "#FFFFFF",
            temperature: "cool",
            intensity: "light",
            undertone: "neutral",
          },
          {
            key: "PURE_BLACK",
            name: "Pure Black",
            hex: "#000000",
            temperature: "cool",
            intensity: "deep",
            undertone: "neutral",
          },
          {
            key: "COOL_PINK",
            name: "Cool Pink",
            hex: "#FF69B4",
            temperature: "cool",
            intensity: "bright",
            undertone: "pink",
          },
          {
            key: "COOL_NAVY",
            name: "Cool Navy",
            hex: "#000080",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
        ],
        sampleClothing: "Warm leather jackets, rich brown boots, vibrant orange scarves",
        characteristics: ["vibrant", "warm", "rich", "intense"],
      },
      DEEP: {
        emoji: "🍂",
        description: `Deep Autumn individuals have deep, rich hues that create a bold and intense appearance.
The Deep Autumn palette features deep, rich shades that complement the natural intensity of Deep Autumn features.`,
        primaryColors: [
          {
            key: "DEEP_MAHOGANY",
            name: "Deep Mahogany",
            hex: "#4A1C17",
            temperature: "warm",
            intensity: "deep",
            undertone: "red",
          },
          {
            key: "DEEP_OLIVE",
            name: "Deep Olive",
            hex: "#556B2F",
            temperature: "warm",
            intensity: "deep",
            undertone: "green",
          },
          {
            key: "DEEP_MOSS",
            name: "Deep Moss",
            hex: "#4D5D53",
            temperature: "warm",
            intensity: "deep",
            undertone: "green",
          },
          {
            key: "BURNT_UMBER",
            name: "Burnt Umber",
            hex: "#8A3324",
            temperature: "warm",
            intensity: "deep",
            undertone: "red",
          },
          {
            key: "DEEP_RUST",
            name: "Deep Rust",
            hex: "#8B2500",
            temperature: "warm",
            intensity: "deep",
            undertone: "orange",
          },
          {
            key: "DARK_BRONZE",
            name: "Dark Bronze",
            hex: "#6E4A1F",
            temperature: "warm",
            intensity: "deep",
            undertone: "brown",
          },
          { key: "WALNUT", name: "Walnut", hex: "#773F1A", temperature: "warm", intensity: "deep", undertone: "brown" },
          {
            key: "DEEP_TERRACOTTA",
            name: "Deep Terracotta",
            hex: "#9B4722",
            temperature: "warm",
            intensity: "deep",
            undertone: "orange",
          },
          { key: "MAROON", name: "Maroon", hex: "#800000", temperature: "warm", intensity: "deep", undertone: "red" },
          {
            key: "FOREST_GREEN",
            name: "Forest Green",
            hex: "#228B22",
            temperature: "warm",
            intensity: "deep",
            undertone: "green",
          },
          {
            key: "DARK_CHESTNUT",
            name: "Dark Chestnut",
            hex: "#5C3317",
            temperature: "warm",
            intensity: "deep",
            undertone: "brown",
          },
          {
            key: "DARK_AMBER",
            name: "Dark Amber",
            hex: "#996515",
            temperature: "warm",
            intensity: "deep",
            undertone: "golden",
          },
        ],
        neutrals: [
          {
            key: "ESPRESSO",
            name: "Espresso",
            hex: "#3C2A21",
            temperature: "warm",
            intensity: "deep",
            undertone: "brown",
          },
          {
            key: "DARK_CHOCOLATE",
            name: "Dark Chocolate",
            hex: "#381819",
            temperature: "warm",
            intensity: "deep",
            undertone: "brown",
          },
          {
            key: "DEEP_CAMEL",
            name: "Deep Camel",
            hex: "#A67B5B",
            temperature: "warm",
            intensity: "deep",
            undertone: "yellow",
          },
          {
            key: "RICH_OLIVE",
            name: "Rich Olive",
            hex: "#6B8E23",
            temperature: "warm",
            intensity: "deep",
            undertone: "green",
          },
        ],
        complementaryColors: [
          {
            key: "DEEP_TEAL",
            name: "Deep Teal",
            hex: "#006666",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "MIDNIGHT_BLUE",
            name: "Midnight Blue",
            hex: "#191970",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "DEEP_LAVENDER",
            name: "Deep Lavender",
            hex: "#6A5ACD",
            temperature: "cool",
            intensity: "deep",
            undertone: "purple",
          },
          {
            key: "COOL_FOREST",
            name: "Cool Forest",
            hex: "#228B22",
            temperature: "cool",
            intensity: "deep",
            undertone: "green",
          },
        ],
        avoidColors: [
          {
            key: "ICY_PINK",
            name: "Icy Pink",
            hex: "#FFDEE9",
            temperature: "cool",
            intensity: "light",
            undertone: "pink",
          },
          {
            key: "PURE_WHITE",
            name: "Pure White",
            hex: "#FFFFFF",
            temperature: "cool",
            intensity: "light",
            undertone: "neutral",
          },
          {
            key: "COOL_BLUE",
            name: "Cool Blue",
            hex: "#00BFFF",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "COOL_FUCHSIA",
            name: "Cool Fuchsia",
            hex: "#FF00FF",
            temperature: "cool",
            intensity: "bright",
            undertone: "pink",
          },
        ],
        sampleClothing: "Deep burgundy dresses, forest green coats, rich golden brown accessories",
        characteristics: ["deep", "warm", "rich", "intense"],
      },
    },
  },
  WINTER: {
    emoji: "❄️",
    subseasons: {
      COOL: {
        description: `Cool Winter individuals exude a crisp and clear presence. Their coloring is stark and contrasting, with strong, cool tones that create a dramatic and striking appearance.
The Cool Winter palette is inspired by the sharp contrasts of a snowy landscape against a clear sky, offering a bold and icy range of colors. Positioned between True Winter and Deep Winter, this sub-season emphasizes clarity and intensity.

Unlike Deep Winter, Cool Winter maintains a lighter edge, ensuring their style remains both impactful and refined.`,
        emoji: "❄️",
        primaryColors: [
          { key: "AZURE", name: "Azure", hex: "#007FFF", temperature: "cool", intensity: "bright", undertone: "blue" },
          { key: "BERRY", name: "Berry", hex: "#990066", temperature: "cool", intensity: "deep", undertone: "pink" },
          {
            key: "BURGUNDY",
            name: "Burgundy",
            hex: "#800020",
            temperature: "cool",
            intensity: "deep",
            undertone: "red",
          },
          {
            key: "CHARCOAL",
            name: "Charcoal",
            hex: "#36454F",
            temperature: "cool",
            intensity: "dark",
            undertone: "blue",
          },
          {
            key: "ICE_BLUE",
            name: "Ice Blue",
            hex: "#A5F2F3",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          { key: "LAPIS", name: "Lapis", hex: "#26619C", temperature: "cool", intensity: "deep", undertone: "blue" },
          {
            key: "MAGENTA",
            name: "Magenta",
            hex: "#CA1F7B",
            temperature: "cool",
            intensity: "bright",
            undertone: "pink",
          },
          {
            key: "SAPPHIRE",
            name: "Sapphire",
            hex: "#0F52BA",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "VIOLET",
            name: "Violet",
            hex: "#8A2BE2",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          { key: "ROSE", name: "Rose", hex: "#C21E56", temperature: "cool", intensity: "medium", undertone: "pink" },
          {
            key: "COOL_SLATE",
            name: "Cool Slate",
            hex: "#708090",
            temperature: "cool",
            intensity: "medium",
            undertone: "blue",
          },
          {
            key: "PERIWINKLE",
            name: "Periwinkle",
            hex: "#CCCCFF",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
        ],
        neutrals: [
          {
            key: "COOL_WHITE",
            name: "Cool White",
            hex: "#F0F8FF",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "CHARCOAL",
            name: "Charcoal",
            hex: "#36454F",
            temperature: "cool",
            intensity: "dark",
            undertone: "blue",
          },
          {
            key: "PEWTER",
            name: "Pewter",
            hex: "#899499",
            temperature: "cool",
            intensity: "medium",
            undertone: "blue",
          },
          { key: "SLATE", name: "Slate", hex: "#708090", temperature: "cool", intensity: "dark", undertone: "blue" },
        ],
        complementaryColors: [
          {
            key: "ICY_PINK",
            name: "Icy Pink",
            hex: "#FFDEE9",
            temperature: "cool",
            intensity: "light",
            undertone: "pink",
          },
          {
            key: "ICY_LAVENDER",
            name: "Icy Lavender",
            hex: "#E6E6FA",
            temperature: "cool",
            intensity: "light",
            undertone: "purple",
          },
          {
            key: "ICY_BLUE",
            name: "Icy Blue",
            hex: "#A5F2F3",
            temperature: "cool",
            intensity: "light",
            undertone: "blue",
          },
          {
            key: "ICY_MINT",
            name: "Icy Mint",
            hex: "#D1FFFA",
            temperature: "cool",
            intensity: "light",
            undertone: "green",
          },
        ],
        avoidColors: [
          {
            key: "WARM_BEIGE",
            name: "Warm Beige",
            hex: "#F5DEB3",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "CAMEL",
            name: "Camel",
            hex: "#C19A6B",
            temperature: "warm",
            intensity: "medium",
            undertone: "yellow",
          },
          {
            key: "GOLDEN_BROWN",
            name: "Golden Brown",
            hex: "#996515",
            temperature: "warm",
            intensity: "medium",
            undertone: "golden",
          },
          {
            key: "TERRACOTTA",
            name: "Terracotta",
            hex: "#E2725B",
            temperature: "warm",
            intensity: "medium",
            undertone: "orange",
          },
        ],
        sampleClothing: "Ice blue cocktail dresses, cool magenta tops, charcoal grey suits",
        characteristics: ["cool", "clear", "bright", "contrasting"],
      },
      TRUE: {
        description: `True Winter individuals possess intense and vibrant coloring. Their palette includes bright and clear hues that complement their dynamic and vivid features.
The True Winter palette is electrifying and bold, featuring high-contrast colors that make a powerful statement. Positioned as the pinnacle of Winter sub-seasons, it embraces maximum clarity and saturation.

Unlike Cool Winter, True Winter thrives on even more pronounced colors, ensuring their style is both unapologetically bold and effortlessly chic.`,
        emoji: "💎",
        primaryColors: [
          {
            key: "ELECTRIC_BLUE",
            name: "Electric Blue",
            hex: "#0055FF",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "HOT_PINK",
            name: "Hot Pink",
            hex: "#FF69B4",
            temperature: "cool",
            intensity: "bright",
            undertone: "pink",
          },
          { key: "RUBY", name: "Ruby", hex: "#E0115F", temperature: "cool", intensity: "deep", undertone: "pink" },
          { key: "BLACK", name: "Black", hex: "#000000", temperature: "cool", intensity: "deep", undertone: "neutral" },
          { key: "FROST", name: "Frost", hex: "#E5F9F9", temperature: "cool", intensity: "light", undertone: "blue" },
          {
            key: "BRIGHT_PURPLE",
            name: "Bright Purple",
            hex: "#6A0DAD",
            temperature: "cool",
            intensity: "bright",
            undertone: "purple",
          },
          {
            key: "LIMESTONE",
            name: "Limestone",
            hex: "#D9D9D6",
            temperature: "cool",
            intensity: "light",
            undertone: "grey",
          },
          {
            key: "EMERALD",
            name: "Emerald",
            hex: "#009B77",
            temperature: "cool",
            intensity: "bright",
            undertone: "green",
          },
          {
            key: "FUCHSIA",
            name: "Fuchsia",
            hex: "#D200D2",
            temperature: "cool",
            intensity: "bright",
            undertone: "pink",
          },
          {
            key: "ULTRAMARINE",
            name: "Ultramarine",
            hex: "#120A8F",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "SILVER",
            name: "Silver",
            hex: "#C0C0C0",
            temperature: "cool",
            intensity: "medium",
            undertone: "grey",
          },
          {
            key: "GRAPHITE",
            name: "Graphite",
            hex: "#1C1C1C",
            temperature: "cool",
            intensity: "dark",
            undertone: "neutral",
          },
        ],
        neutrals: [
          {
            key: "BRIGHT_WHITE",
            name: "Bright White",
            hex: "#FFFFFF",
            temperature: "cool",
            intensity: "light",
            undertone: "neutral",
          },
          { key: "BLACK", name: "Black", hex: "#000000", temperature: "cool", intensity: "deep", undertone: "neutral" },
          {
            key: "COOL_GREY",
            name: "Cool Grey",
            hex: "#A9A9A9",
            temperature: "cool",
            intensity: "medium",
            undertone: "blue",
          },
          {
            key: "SILVER",
            name: "Silver",
            hex: "#C0C0C0",
            temperature: "cool",
            intensity: "medium",
            undertone: "grey",
          },
        ],
        complementaryColors: [
          { key: "CYAN", name: "Cyan", hex: "#00FFFF", temperature: "cool", intensity: "bright", undertone: "blue" },
          {
            key: "BRIGHT_MAGENTA",
            name: "Bright Magenta",
            hex: "#FF00FF",
            temperature: "cool",
            intensity: "bright",
            undertone: "pink",
          },
          {
            key: "ELECTRIC_BLUE",
            name: "Electric Blue",
            hex: "#0055FF",
            temperature: "cool",
            intensity: "bright",
            undertone: "blue",
          },
          {
            key: "BRIGHT_PURPLE",
            name: "Bright Purple",
            hex: "#A020F0",
            temperature: "cool",
            intensity: "bright",
            undertone: "purple",
          },
        ],
        avoidColors: [
          { key: "CREAM", name: "Cream", hex: "#FFFDD0", temperature: "warm", intensity: "light", undertone: "yellow" },
          {
            key: "WARM_CAMEL",
            name: "Warm Camel",
            hex: "#C19A6B",
            temperature: "warm",
            intensity: "medium",
            undertone: "yellow",
          },
          {
            key: "RUST_ORANGE",
            name: "Rust Orange",
            hex: "#B7410E",
            temperature: "warm",
            intensity: "medium",
            undertone: "orange",
          },
          {
            key: "WARM_GOLD",
            name: "Warm Gold",
            hex: "#FFD700",
            temperature: "warm",
            intensity: "bright",
            undertone: "yellow",
          },
        ],
        sampleClothing: "Crisp white shirts, black trousers, bold red accessories, jewel-toned evening wear",
        characteristics: ["high-contrast", "clear", "dramatic", "cool", "vivid"],
      },
      DEEP: {
        description: `Deep Winter individuals have deep, rich hues that create a bold and intense appearance.
The Deep Winter palette features deep, rich shades that complement the natural intensity of Deep Winter features.
This sub-season is positioned between True Winter and Deep Winter on the seasonal flow chart.`,
        emoji: "🧊",
        primaryColors: [
          {
            key: "DEEP_NAVY",
            name: "Deep Navy",
            hex: "#000080",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "DEEP_RUBY",
            name: "Deep Ruby",
            hex: "#9B111E",
            temperature: "cool",
            intensity: "deep",
            undertone: "red",
          },
          {
            key: "DEEP_EMERALD",
            name: "Deep Emerald",
            hex: "#046307",
            temperature: "cool",
            intensity: "deep",
            undertone: "green",
          },
          {
            key: "DEEP_TEAL",
            name: "Deep Teal",
            hex: "#004B49",
            temperature: "cool",
            intensity: "deep",
            undertone: "green",
          },
          {
            key: "DARK_OXBLOOD",
            name: "Dark Oxblood",
            hex: "#4A0E0E",
            temperature: "cool",
            intensity: "deep",
            undertone: "red",
          },
          {
            key: "AUBERGINE",
            name: "Aubergine",
            hex: "#580F41",
            temperature: "cool",
            intensity: "deep",
            undertone: "purple",
          },
          {
            key: "DEEP_SAPPHIRE",
            name: "Deep Sapphire",
            hex: "#082567",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "DEEP_CHARCOAL",
            name: "Deep Charcoal",
            hex: "#2B2B2B",
            temperature: "cool",
            intensity: "dark",
            undertone: "neutral",
          },
          {
            key: "DEEP_MAGENTA",
            name: "Deep Magenta",
            hex: "#8B008B",
            temperature: "cool",
            intensity: "deep",
            undertone: "purple",
          },
          { key: "WINE", name: "Wine", hex: "#722F37", temperature: "cool", intensity: "deep", undertone: "red" },
          {
            key: "DEEP_SLATE",
            name: "Deep Slate",
            hex: "#2F4F4F",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "TRUE_RED",
            name: "True Red",
            hex: "#C8102E",
            temperature: "cool",
            intensity: "bright",
            undertone: "red",
          },
        ],
        neutrals: [
          { key: "BLACK", name: "Black", hex: "#000000", temperature: "cool", intensity: "deep", undertone: "neutral" },
          {
            key: "DEEP_CHARCOAL",
            name: "Deep Charcoal",
            hex: "#2B2B2B",
            temperature: "cool",
            intensity: "deep",
            undertone: "neutral",
          },
          {
            key: "ESPRESSO",
            name: "Espresso",
            hex: "#3C2A21",
            temperature: "cool",
            intensity: "deep",
            undertone: "neutral",
          },
          {
            key: "PEWTER_GREY",
            name: "Pewter Grey",
            hex: "#899499",
            temperature: "cool",
            intensity: "medium",
            undertone: "blue",
          },
        ],
        complementaryColors: [
          {
            key: "DEEP_PLUM",
            name: "Deep Plum",
            hex: "#4E2A5A",
            temperature: "cool",
            intensity: "deep",
            undertone: "purple",
          },
          {
            key: "DARK_CHERRY",
            name: "Dark Cherry",
            hex: "#5C0F1B",
            temperature: "cool",
            intensity: "deep",
            undertone: "red",
          },
          {
            key: "PRUSSIAN_BLUE",
            name: "Prussian Blue",
            hex: "#003153",
            temperature: "cool",
            intensity: "deep",
            undertone: "blue",
          },
          {
            key: "DEEP_FOREST",
            name: "Deep Forest",
            hex: "#0B3D2E",
            temperature: "cool",
            intensity: "deep",
            undertone: "green",
          },
        ],
        avoidColors: [
          {
            key: "WARM_IVORY",
            name: "Warm Ivory",
            hex: "#FFFFF0",
            temperature: "warm",
            intensity: "light",
            undertone: "yellow",
          },
          {
            key: "WARM_CAMEL",
            name: "Warm Camel",
            hex: "#C19A6B",
            temperature: "warm",
            intensity: "medium",
            undertone: "yellow",
          },
          {
            key: "TERRACOTTA",
            name: "Terracotta",
            hex: "#E2725B",
            temperature: "warm",
            intensity: "medium",
            undertone: "orange",
          },
          {
            key: "WARM_MOSS",
            name: "Warm Moss",
            hex: "#8A9A5B",
            temperature: "warm",
            intensity: "medium",
            undertone: "green",
          },
        ],
        sampleClothing: "Deep purple evening gowns, navy suits, emerald green accessories",
        characteristics: ["deep", "rich", "dramatic", "mysterious"],
      },
    },
  },
};

/**
 * TODO
 * - Write Script to generate clothes for every season and subseason
 * Write cloud function to apply all of those clothes to a pereson
 * Write cloud function to have AI rank the clothing on that person
 */

export type PricingTier = {
  id: string;
  title: string;
  price: number;
  description: string;
  features: { category: string; items: string[] }[];
  turnaround: string;
  recommended?: boolean;
};

// Color recommendation type used across makeup recommendations
export type ColorRecommendation = {
  name: string;
  hex: string;
  key: string;
  temperature: string;
  intensity: string;
  undertone: string;
};

// Metal recommendations for jewelry
export type MetalRecommendations = {
  best: string[];
  good: string[];
  avoid: string[];
  explanation: string;
};

// Hair color recommendations
export type HairColorRecommendations = {
  flattering: string[];
  avoid: string[];
  explanation: string;
};

// Personalized insights about why this season works
export type PersonalizedInsights = {
  whyThisSeason: string;
  yourBestFeature: string;
  avoidBecause: string;
  colorMistakes?: Array<{
    mistake: string;
    why: string;
  }>;
  shoppingTips?: string[];
  signatureTip?: string;
};

// Color combination for outfits
export type ColorCombination = {
  name: string;
  colors: string[]; // hex codes
  description: string;
};

// Neutrals guide specific to their season
export type NeutralsGuide = {
  yourBlack: string;
  yourWhite: string;
  yourGray: string;
  yourNavy: string;
  yourBrown: string;
  explanation: string;
};

// Color personality summary
export type ColorPersonality = {
  tagline: string;
  description: string;
  celebrities: string[];
};

export interface ColorAnalysis {
  analysisId: string;
  status: string;
  error?: string;
  results?: {
    season: string;
    subSeason: string;
    worstSeason?: string;
    worstSubSeason?: string;
    explanation: string;
    undertone: string;
    contrast?: string;
    skinTone?: string;
    skinToneHex?: string;
    hairColor?: string;
    hairColorHex?: string;
    eyeColor?: string;
    eyeColorHex?: string;
    contrastLevel?: string;
    confidenceLevel?: number;
    secondarySeason?: string;
    secondarySubSeason?: string;
    secondaryConfidence?: number;
    gender?: string;
    makeupRecommendations?: {
      blush: {
        recommendedColors: ColorRecommendation[];
        explanation: string;
      };
      lipstick: {
        recommendedColors: ColorRecommendation[];
        explanation: string;
      };
      eyeshadow: {
        recommendedPalettes: Array<{
          name: string;
          colors: ColorRecommendation[];
        }>;
        explanation: string;
      };
    };
    // New enhanced fields
    metalRecommendations?: MetalRecommendations;
    hairColorRecommendations?: HairColorRecommendations;
    personalizedInsights?: PersonalizedInsights;
    colorCombinations?: ColorCombination[];
    neutralsGuide?: NeutralsGuide;
    colorPersonality?: ColorPersonality;
    colorSuperpower?: string;
    quickGuide?: {
      wearMoreOf: string[];
      useWithCaution: string[];
      avoid: string[];
    };
    productRecommendations?: {
      category: string;
      brand: string;
      product: string;
      shade: string;
      whyItWorks: string;
    }[];
  };
  processedOutputs: { outfitName: string; outputPath: string }[];
  hairColorPreviews?: { color: string; path: string }[];
  lipstickPreviews?: { color: string; hex: string; path: string }[];
  hairstylePreviews?: { key: string; name: string; path: string }[];
  comparativeDrapes?: {
    best: { name: string; hex: string; path: string }[];
    worst: { name: string; hex: string; path: string }[];
  };

  // Customer / payment metadata (written at upload / payment time)
  customerEmail?: string;
  createdAt?: any;

  // Refund-eligibility + re-run tracking.
  // refundEligible defaults to true for a first, untouched analysis. The moment
  // a customer re-runs (after accepting the non-refundable waiver) it is set to
  // false and refundLockedAt is stamped. These are written ONLY server-side
  // (app/api/rerun-analysis) so the value is trustworthy in a dispute.
  refundEligible?: boolean;
  refundLockedAt?: any;
  rerunCount?: number;
  // Prior results are archived here before each re-run overwrites them, so the
  // full history survives for dispute evidence.
  rerunHistory?: {
    results?: ColorAnalysis["results"];
    completedAt?: any;
    archivedAt?: any;
  }[];

  // Stamped by app/api/stripe-refund-webhook when Stripe reports a full refund
  // (charge.refunded). Presence gates the results page — refunded customers no
  // longer see their analysis.
  refundedAt?: any;
}

// Statuses used across the analysis lifecycle. Kept as string on the doc for
// backwards-compat; this union documents the known values.
export type AnalysisStatus =
  | "PENDING_PAYMENT"
  | "IN_PROGRESS"
  | "GENERATING_OUTFITS"
  | "READY_FOR_OUTFIT_APPLICATION"
  | "GENERATING_DRAPES"
  | "COMPLETE"
  | "AWAITING_RERUN_PHOTOS"
  | "ERROR";

// Audit-log event names written to the refundAuditLog collection.
//
// Delivery events (the customer RECEIVED / TOOK their results) are the strongest
// dispute evidence — "downloaded the full PDF report" beats "viewed the page".
// Some delivery happens server-side (the Cloud Function emails the report), so
// those events are reported back to /api/refund-audit by trusted callers.
export type RefundAuditEvent =
  // Engagement (browser)
  | "results_viewed"
  | "rerun_opened"
  | "waiver_accepted"
  | "rerun_submitted"
  // Delivery / "they took the goods"
  | "pdf_download_started"
  | "pdf_downloaded"
  | "zip_downloaded"
  | "image_downloaded"
  | "share_link_copied"
  // Server-reported delivery (Cloud Function -> /api/refund-audit)
  | "report_email_sent"
  // Refund lifecycle (Stripe webhook -> written server-side)
  | "refund_processed";
